import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useRef, useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { Eye } from 'lucide-react';
import CommonDrawerLayout from '../../../common_components/CommonDrawerLayout';
import AppButton from '../../../common_components/AppButton';
import { toast } from '../../../common_components/Toast/ToastUtils';
import { useDispatch } from 'react-redux';
import { bulkUploadOpportunitiesThunk, checkBulkUploadThunk } from '../../../redux/opportunitySlice';
import { Alert, AlertTitle, List, ListItem, ListItemText } from '@mui/material';
import { AlertCircle, CheckCircle } from 'lucide-react';
import HoverTooltip from '../../../common_components/hoverOverlay/HoverTooltip';
const UploadDirectoryDrawer = ({ open, onClose, onSuccess, moduleKey }) => {
    const dispatch = useDispatch();
    const [files, setFiles] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    const [validationErrors, setValidationErrors] = useState([]);
    const [isValidated, setIsValidated] = useState(false);
    const fileRef = useRef(null);
    const openFilePicker = () => fileRef.current?.click();
    const handleFileUpload = (e) => {
        const uploadedFiles = Array.from(e.target.files || []);
        const validFiles = uploadedFiles.filter(file => {
            const isExcel = file.name.endsWith('.xlsx') || file.name.endsWith('.xls') || file.name.endsWith('.csv');
            if (!isExcel) {
                toast.error(`File ${file.name} is not a valid Excel/CSV file.`);
            }
            return isExcel;
        });
        if (validFiles.length > 0) {
            setFiles(prev => {
                const updatedFiles = [...prev, ...validFiles];
                // ✅ Auto-check errors for all files
                autoCheck(updatedFiles);
                return updatedFiles;
            });
        }
        if (fileRef.current)
            fileRef.current.value = '';
    };
    const removeFile = (index) => {
        setFiles(prev => {
            const updatedFiles = prev.filter((_, i) => i !== index);
            if (updatedFiles.length > 0) {
                autoCheck(updatedFiles);
            }
            else {
                setIsValidated(false);
                setValidationErrors([]);
            }
            return updatedFiles;
        });
    };
    const autoCheck = async (filesToCheck) => {
        try {
            setIsValidating(true);
            setIsValidated(false);
            setValidationErrors([]);
            const formData = new FormData();
            filesToCheck.forEach(file => {
                formData.append('file', file);
            });
            const response = await dispatch(checkBulkUploadThunk(formData)).unwrap();
            if (response.success && (!response.errors || response.errors.length === 0)) {
                setIsValidated(true);
                setValidationErrors([]);
            }
            else {
                setIsValidated(false);
                setValidationErrors(response.errors || ['Validation failed']);
            }
        }
        catch (error) {
            setIsValidated(false);
            console.error("DEBUG - Validation Error Caught:", error);
            // Priority 1: Check for explicit errors array (standard for bulk failures)
            if (error?.errors && Array.isArray(error.errors)) {
                setValidationErrors(error.errors);
            }
            // Priority 2: Check for header errors in error.data
            else if (error?.data && Array.isArray(error.data)) {
                setValidationErrors(error.data);
            }
            // Priority 3: Check for row errors in error.data.errors
            else if (error?.data?.errors && Array.isArray(error.data.errors)) {
                setValidationErrors(error.data.errors);
            }
            // Priority 4: Check if error itself is the array
            else if (Array.isArray(error)) {
                setValidationErrors(error);
            }
            // Priority 5: Single error message in various fields
            else if (error?.message && error?.message !== "Validation failed") {
                // If it's a header error but message is just "Invalid CSV headers", 
                // we've already handled it above if data was an array.
                // If we get here, just show the message.
                setValidationErrors([error.message]);
            }
            else if (error?.error) {
                setValidationErrors([error.error]);
            }
            // Fallback: If nothing else but we have a general message, use it
            else if (error?.message) {
                setValidationErrors([error.message]);
            }
            else if (typeof error === 'string' && error !== "Validation failed") {
                setValidationErrors([error]);
            }
            else {
                setValidationErrors(['Validation failed for one or more records. Please check the file contents.']);
            }
        }
        finally {
            setIsValidating(false);
        }
    };
    const getModuleConfig = () => {
        switch (moduleKey) {
            case 'content_opportunities':
                return {
                    title: 'Import Content Opportunity',
                    sampleFile: '/assets/sample_content_opportunity.xlsx',
                    sampleName: 'sample_content_opportunity.xlsx',
                    instructions: [
                        { label: 'Primary Lead', text: 'Enter the full name of the primary lead.' },
                        { label: 'Category', text: 'Select a category from the provided list.' },
                        { label: 'Contact Name', text: 'Enter the name of the contact person.' },
                        { label: 'Contact Email', text: 'Enter the email of the contact person.' },
                        { label: 'Type', text: 'Select a type (Creator, Publisher, or Platform/Tool).' },
                        { label: 'Website', text: 'Enter the full website URL.' },
                        { label: 'Publisher', text: 'Enter the publisher/name.' },
                        { label: 'Date', text: 'Enter the date in MM/DD/YYYY format.' },
                    ]
                };
            case 'partner_directory':
                return {
                    title: 'Import Partner Directory',
                    sampleFile: '/assets/sample_Partner_directory.xlsx',
                    sampleName: 'sample_Partner_directory.xlsx',
                    instructions: [
                        { label: 'Primary Lead', text: 'Enter the full name of the primary lead.' },
                        { label: 'Partner Name', text: 'Enter the full name of the partner.' },
                        { label: 'Category', text: 'Select a category from the provided list.' },
                        { label: 'Date', text: 'Enter the date in MM/DD/YYYY format.' },
                        { label: 'Type', text: 'Select a type from the provided list.' },
                        { label: 'Tag', text: 'Add comma-separated tags.' },
                    ]
                };
            default:
                return {
                    title: 'Import Creator Directory',
                    sampleFile: '/assets/sample.xlsx',
                    sampleName: 'sample.xlsx',
                    instructions: [
                        { label: 'Primary Lead', text: 'Enter the full name of the primary lead, including the first and last name.' },
                        { label: 'Category(column B)', text: 'Select a category from the provided list in the sample file.' },
                        { label: 'Date', text: 'Enter the date in MM/DD/YYYY format.' },
                        { label: 'Type', text: 'Select a type from the provided list in the sample file.' },
                        { label: 'Tag', text: 'Add comma-separated tags.' },
                    ]
                };
        }
    };
    const config = getModuleConfig();
    const handleSave = async () => {
        if (files.length === 0) {
            toast.error('Please select at least one file to upload.');
            return;
        }
        try {
            setIsSaving(true);
            const formData = new FormData();
            files.forEach(file => {
                formData.append('file', file);
            });
            await dispatch(bulkUploadOpportunitiesThunk(formData)).unwrap();
            toast.success('Directory uploaded successfully!');
            onSuccess();
            onClose();
            setFiles([]);
        }
        catch (error) {
            toast.error(error?.message || 'Failed to upload directory.');
        }
        finally {
            setIsSaving(false);
        }
    };
    const formatBytes = (bytes) => {
        if (bytes === 0)
            return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    };
    const formatDate = (date) => {
        return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
    };
    return (_jsx(CommonDrawerLayout, { open: open, onClose: onClose, title: config.title, headerActions: (isValidated && validationErrors.length === 0) ? (_jsx(Box, { sx: { display: 'flex', gap: 1 }, children: _jsx(AppButton, { variant: "contained", color: "primary", className: "app-common-button", onClick: handleSave, disabled: isSaving || isValidating, sx: {
                    bgcolor: '#FF008E',
                    '&:hover': { bgcolor: '#E6007A' }
                }, children: isSaving ? 'Uploading...' : 'Save' }) })) : null, width: "md", children: _jsxs(Box, { sx: { p: 1, display: 'flex', flexDirection: 'column', gap: 2 }, children: [_jsxs(Box, { sx: {
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        p: 1,
                        bgcolor: '#ECFDF3',
                        border: '1px solid #D1FADF',
                        borderRadius: '8px'
                    }, children: [_jsx(Box, { sx: {
                                width: 20, height: 20, borderRadius: '50%', bgcolor: '#32D583',
                                display: 'flex', alignItems: 'center', justifyContent: 'center'
                            }, children: _jsx("svg", { width: "12", height: "12", viewBox: "0 0 12 12", fill: "none", children: _jsx("path", { d: "M10 3L4.5 8.5L2 6", stroke: "white", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" }) }) }), _jsxs(Typography, { sx: { fontSize: '14px', color: '#067647' }, children: ["Please upload an Excel file in the required format. You can download a ", _jsx("a", { href: config.sampleFile, download: config.sampleName, style: { color: '#067647', fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }, children: "sample file" }), " for reference."] })] }), _jsxs(Box, { sx: { p: 1, border: '1px solid #EAECF0', borderRadius: '8px' }, children: [_jsx(Typography, { sx: { fontSize: '14px', fontWeight: 600, color: '#EE2A7B', mb: 2 }, children: "File Format Instructions" }), _jsx(Box, { component: "ul", sx: { m: 0, p: 0, pl: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }, children: config.instructions.map((item, idx) => (_jsxs(Box, { component: "li", sx: { fontSize: '13px', color: '#667085' }, children: [_jsxs("span", { style: { fontWeight: 600, color: '#344054' }, children: [item.label, ":"] }), " ", item.text] }, idx))) })] }), _jsxs(Box, { sx: { p: 1, border: '1px solid #EAECF0', borderRadius: '8px' }, children: [_jsx(Typography, { sx: { fontSize: '14px', fontWeight: 600, color: '#EE2A7B', mb: 2 }, children: "Upload File" }), _jsxs(Box, { onClick: !(isValidating || files.length > 0) ? openFilePicker : undefined, sx: {
                                border: '1px dashed #D0D5DD',
                                borderRadius: '8px',
                                p: 4,
                                textAlign: 'center',
                                cursor: (isValidating || files.length > 0) ? 'not-allowed' : 'pointer',
                                bgcolor: (isValidating || files.length > 0) ? '#F9FAFB' : 'transparent',
                                opacity: (isValidating || files.length > 0) ? 0.7 : 1,
                                pointerEvents: (isValidating || files.length > 0) ? 'none' : 'auto',
                                '&:hover': { bgcolor: '#F9FAFB' }
                            }, children: [_jsx("input", { ref: fileRef, type: "file", onChange: handleFileUpload, style: { display: 'none' }, accept: ".xlsx, .xls, .csv", disabled: isValidating || files.length > 0 }), _jsx(Box, { sx: {
                                        width: 40, height: 40, borderRadius: '50%', bgcolor: '#F2F4F7',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        mx: 'auto', mb: 1.5,
                                        filter: (isValidating || files.length > 0) ? 'grayscale(100%)' : 'none'
                                    }, children: _jsx("img", { src: "/assets/upload.svg", alt: "upload", style: { width: 20, height: 20 } }) }), _jsx(Typography, { sx: { fontSize: '14px', color: '#475467' }, children: isValidating ? 'Checking for errors...' : (files.length > 0) ? (validationErrors.length > 0 ? "Validation Failed" : 'File successfully validated') : (_jsxs(_Fragment, { children: ["Drag and Drop or ", _jsx("span", { style: { color: '#EE2A7B', fontWeight: 600 }, children: "Browse" }), " to upload"] })) }), _jsx(Typography, { sx: { fontSize: '12px', color: '#667085', mt: 0.5 }, children: isValidating ? 'Please wait...' : (files.length > 0) ? (validationErrors.length > 0 ? "Please check errors" : 'Ready to import') : 'Supported Format: Excel' })] }), validationErrors.length > 0 && (_jsxs(Alert, { severity: "error", sx: { mt: 1, borderRadius: '8px' }, icon: _jsx(AlertCircle, { size: 20 }), children: [_jsx(AlertTitle, { sx: { fontSize: '14px', fontWeight: 600 }, children: validationErrors.length > 1
                                        ? `Validation Errors (${validationErrors.length})`
                                        : 'Validation Error' }), validationErrors.length === 1 ? (_jsx(Typography, { sx: { fontSize: '12px', color: '#B42318' }, children: validationErrors[0] })) : (_jsx(HoverTooltip, { title: _jsx(Box, { sx: { p: 1, maxHeight: '300px', overflowY: 'auto' }, children: _jsx(List, { dense: true, sx: { p: 0 }, children: validationErrors.map((error, idx) => (_jsx(ListItem, { sx: { p: 0, mb: 0.5 }, children: _jsx(ListItemText, { primary: error, primaryTypographyProps: { fontSize: '12px', color: '#fff' } }) }, idx))) }) }), placement: "bottom-start", children: _jsxs(Typography, { sx: {
                                            fontSize: '12px',
                                            color: '#B42318',
                                            cursor: 'help',
                                            textDecoration: 'underline dotted'
                                        }, children: ["Validation failed with ", validationErrors.length, " issues. Hover to view details."] }) }))] })), isValidated && validationErrors.length === 0 && files.length > 0 && (_jsxs(Alert, { severity: "success", sx: { mt: 2, borderRadius: '8px' }, icon: _jsx(CheckCircle, { size: 20 }), children: [_jsx(AlertTitle, { sx: { fontSize: '14px', fontWeight: 600 }, children: "Ready to Import!" }), _jsx(Typography, { sx: { fontSize: '12px' }, children: "File validation finished. You can now click \"Save\" to import the creators." })] })), _jsx(Box, { sx: { mt: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }, children: files.map((file, idx) => (_jsxs(Box, { sx: {
                                    p: 1,
                                    border: '1px solid #EAECF0',
                                    borderRadius: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1.5 }, children: [_jsx("img", { src: "/assets/xlicon.svg", alt: "" }), _jsxs(Box, { children: [_jsx(Typography, { sx: { fontSize: '13px', fontWeight: 500, color: '#344054' }, children: file.name }), _jsxs(Typography, { sx: { fontSize: '11px', color: '#667085' }, children: ["Date: ", formatDate(new Date()), "  \u2022  Size: ", formatBytes(file.size)] }), _jsx(Typography, { sx: { fontSize: '11px', fontWeight: 600, color: '#12B76A', mt: 0.5 }, children: "Completed" })] })] }), _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: [_jsx(IconButton, { size: "small", onClick: () => removeFile(idx), children: _jsx("img", { src: "/assets/delete.svg", alt: "Delete", style: { width: 16 } }) }), _jsx(IconButton, { size: "small", children: _jsx(Eye, { size: 16, color: "#667085" }) })] })] }, idx))) })] })] }) }));
};
export default UploadDirectoryDrawer;
